import { _ } from "../../vendor";
import { CollectionElement } from "js/elements/base/CollectionElement";
import * as geom from "js/core/utilities/geom";
import ConnectorGroup from "js/elements/elements/connectors/ConnectorGroup";
import {
    BulletTextNodeElement,
    ContentAndTextNodeElement,
    ContentNodeElement,
    FlexCircleNodeElement, getNodeElementFromType,
    NumberedTextNodeElement,
    TextInShapeNodeElement,
    TextNodeElement
} from "js/elements/base/NodeElement";
import { NodeType } from "common/constants";
import { getContentItemFromType } from "js/elements/base/ContentItem";

export class AnnotationLayer extends CollectionElement {
    get minItemCount() {
        return 0;
    }

    get allowDragDropElements() {
        return false;
    }

    get canSelect() {
        return false;
    }

    getChildItemType(model) {
        if (model.blocks?.length) {
            return getNodeElementFromType(model.nodeType || NodeType.BOX);
        } else {
            return getContentItemFromType(model.nodeType || NodeType.BOX);
        }
    }

    getAllowedNodeTypes() {
        return [NodeType.TEXT, NodeType.BOX, NodeType.CAPSULE, NodeType.CIRCLE, NodeType.BULLET_TEXT, NodeType.CONTENT_AND_TEXT, NodeType.NUMBERED_TEXT, NodeType.CONTENT, NodeType.LINE];
    }

    getItemSelectionUIType(node) {
        return "AnnotationItemSelection";
    }

    getItemRolloverUIType(node) {
        return "AnnotationItemRollover";
    }

    getChildOptions(model) {
        return { allowConnection: false };
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition, true);
    }

    get constrainNodesToBounds() {
        return false;
    }

    get canRefreshElement() {
        return true;
    }

    resetUserColors() {
        return; // don't reset user colors on annnotations
    }

    _build() {
        super._build();

        if (!this.model.connections) {
            this.model.connections = {
                items: []
            };
        }

        this.connectors = this.addElement("connectors", () => ConnectorGroup, {
            model: this.model.connections,
            containerElement: this,
            startPointsAreLocked: true
        });
        this.connectors.layer = -1;
    }

    _calcProps(props, options) {
        const { size } = props;

        let shouldUpdateModel = false;
        const containerBounds = new geom.Rect(0, 0, size);
        for (const item of this.itemElements) {
            const itemProps = item.calcProps(size);
            if (item.model.snapOptions) {
                const sourceElement = this.canvas.getElementByUniquePath(item.model.snapOptions.elementId);
                const snapPoint = sourceElement.getSnapPoint(item);
                itemProps.bounds = new geom.Rect(snapPoint.x, snapPoint.y, itemProps.size);
            } else {
                itemProps.bounds = new geom.Rect(size.width * item.model.x, size.height * item.model.y, itemProps.size);
            }

            // adjustedItemBounds will respect the node's registration point
            const adjustedItemBounds = item.bounds;
            // Fitting the node
            if (this.constrainNodesToBounds && !containerBounds.contains(adjustedItemBounds)) {
                const offset = adjustedItemBounds.fitInRect(containerBounds).position.minus(adjustedItemBounds.position);
                itemProps.bounds = itemProps.bounds.offset(offset);
                item.model.x += offset.x / size.width;
                item.model.y += offset.y / size.height;
                // If the item is being dragged, then its model will be updated
                // when dragging is finished
                if (!item.isDragging && !item.isDragResizing) {
                    shouldUpdateModel = true;
                }
            }
        }

        if (shouldUpdateModel) {
            this.canvas.layouter.runPostRender(() => this.canvas.saveCanvasModel());
        }

        const connectorProps = this.connectors.calcProps(size);
        connectorProps.bounds = new geom.Rect(0, 0, size);

        return { size };
    }

    deleteItem(itemId) {
        // delete any connectors that referenced the deleted item
        _.remove(this.model.connections.items, connector => connector.source == itemId);
        super.deleteItem(itemId);
    }

    async pasteFromClipboard({ dataType, model }) {
        if (dataType === "element") {
            const modelToPaste = _.cloneDeep(model);
            while (this.itemElements.filter(item => item.model.x === modelToPaste.x && item.model.y === modelToPaste.y).length > 0) {
                modelToPaste.x += 0.05;
                modelToPaste.y += 0.05;
            }
            delete modelToPaste.id;

            const { id } = this.addItem(modelToPaste);
            await this.canvas.updateCanvasModel(false);
            return this.getChild(id);
        }
    }

    getAnimations() {
        const animations = [];

        const connectorsWithNode = [];
        this.itemElements.forEach(node => {
            const nodeAnimations = node.getAnimations();
            animations.push(...nodeAnimations);

            const nodeName = node.animationElementName;
            node.connectorsFromNode.forEach(connector => {
                connectorsWithNode.push(connector);

                const connectorName = `← ${nodeName}`;
                const connectorAnimations = connector.getAnimations();
                connectorAnimations.forEach(animation => animation.elementName = connectorName);
                animations.push(...connectorAnimations);
            });
        });

        this.connectors.itemElements
            .filter(connector => !connectorsWithNode.includes(connector))
            .forEach((connector, idx) => {
                const connectorAnimations = connector.getAnimations();
                connectorAnimations.forEach(animation => animation.elementName = `Line #${idx + 1}`);
                animations.push(...connectorAnimations);
            });

        return animations;
    }
}

export const elements = {
    AnnotationLayer
};
